import { lazy } from 'react'

import { useLocation } from 'react-router'

import { type IBreadcrumbsProps } from '@webapp/core/components/Breadcrumbs'
import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { useReportFilterCarryOver } from '@webapp/modules/reports/hooks/useReportFilterCarryOver'
import { parseExportHistoryReferrer } from '@webapp/modules/reports/utils/parseExportHistoryReferrer'
import messages from './messages'

const ReportExportHistoryViewChunk = lazy(() =>
  import('@webapp/modules/reports/feature-sets/ReportExportHistoryView').then(
    ({ ReportExportHistoryView }) => ({
      default: ReportExportHistoryView,
    })
  )
)

export const ReportExportHistoryPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const location = useLocation()
  const { withReportFilters } = useReportFilterCarryOver()

  // Back-breadcrumb to the originating report, reconstructed from the
  // referrer query param — including the details view when the referrer
  // carried an entity id and name.
  const { reportConfig, entityId, entityName } = parseExportHistoryReferrer(
    location.search
  )

  const breadcrumbs = [
    ...(reportConfig
      ? [
          {
            link: withReportFilters(url(reportConfig.aggregatePage)),
            name: intl.formatMessage(reportConfig.titleKey),
            escapeCaseFormatting: true,
          },
          // An id-only referrer (the name had not loaded) has no label to
          // tell the details crumb apart from the report one, so it is left out.
          ...(entityId && entityName
            ? [
                {
                  link: withReportFilters(
                    url(reportConfig.detailPage, { id: String(entityId) })
                  ),
                  name: entityName,
                  escapeCaseFormatting: true,
                },
              ]
            : []),
        ]
      : []),
    { name: intl.formatMessage(messages.pageTitle) },
  ] satisfies IBreadcrumbsProps['crumbs']

  return (
    <Page
      pageTitle={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={breadcrumbs}
    >
      <ReportExportHistoryViewChunk />
    </Page>
  )
}
